import styled from '@emotion/styled';
import { ifNotProp, ifProp } from 'styled-tools';
import { LoadingCircle } from '~/shared/components/Loading/LoadingCircle';

export type StyledCheckboxWrapperProps = {
	valid?: boolean;
	disabled?: boolean;
};

export const StyledCheckboxWrapper = styled.div<StyledCheckboxWrapperProps>(
	({ theme }) => ({
		'--checkbox-cursor': 'pointer',
		display: 'grid',
		gridTemplateColumns: `${theme.checkboxSize} 1fr`,
	}),
	ifNotProp('valid', ({ theme }) => ({
		[`${StyledCheckboxIndicator}`]: {
			outlineColor: theme.colors.negative,
		},
	})),
	ifProp('disabled', {
		'--checkbox-cursor': 'not-allowed',
		opacity: 0.5,

		svg: {
			opacity: 1,
		},
	}),
);

export const StyledCheckbox = styled.input(({ type }) => ({
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	margin: 0,
	padding: 0,
	zIndex: 1,
	cursor: 'inherit',
	appearance: 'none',
	webkitAppearance: 'none',
	backgroundColor: 'transparent',
	outlineOffset: '3px',
	border: 0,
	borderRadius: type === 'radio' ? '50%' : 'inherit',
}));

export const StyledLabel = styled.label(({ theme }) => ({
	color: theme.colors.black,
	fontSize: theme.fontSizes.xs,
	display: 'flex',
	alignItems: 'center',
	cursor: 'var(--checkbox-cursor)',
	padding: `1px 0 0 ${theme.space[2]}`,
	lineHeight: theme.checkboxSize,
	minHeight: theme.checkboxSize,

	'*': {
		fontSize: `${theme.fontSizes.xs} !important`,
		lineHeight: theme.checkboxSize,

		'&:last-of-type': {
			marginBottom: 0,
		},
	},
}));

export const StyledLoadingCircle = styled(LoadingCircle)(({ theme }) => ({
	marginLeft: theme.space[2],
}));

export const StyledLabelSuffix = styled.span(({ theme }) => ({
	marginLeft: theme.space[2],
	color: theme.colors.grey60,
}));

export type StyledCheckboxElementProps = { round?: boolean };

export const StyledCheckboxElement = styled.div<StyledCheckboxElementProps>(
	{
		position: 'relative',
		boxSizing: 'border-box',
		cursor: 'var(--checkbox-cursor)',
	},
	ifProp('round', {
		borderRadius: '100%',
	}),
);

export const StyledCheckboxIndicator = styled.div(({ theme }) => ({
	width: theme.checkboxSize,
	height: theme.checkboxSize,
	outline: `${theme.checkboxBorderWidth} solid ${theme.colors.grey60}`,
	outlineOffset: `-${theme.checkboxBorderWidth}`,
	borderRadius: 'inherit',

	svg: {
		position: 'absolute',
		top: 0,
		width: '20px',
		height: '20px',
		opacity: 0,
	},

	'input:checked + &': {
		background: theme.colors.buttonDark,
		color: theme.colors.light,

		svg: {
			opacity: 1,
		},
	},
}));

export const StyledCheckboxColorIndicator = styled.span(({ theme }) => ({
	display: 'inline-block',
	marginRight: theme.space[1],
	width: '13px',
	height: '13px',
	borderRadius: '100%',
	border: `1px solid ${theme.colors.border}`,
}));

export const StyledCheckboxHelpTexts = styled.div(({ theme }) => ({
	marginLeft: `calc(25px + ${theme.space[1]})`,
}));
